import type { LedgerFinding } from "../api/codeReview";
import type { BobPosture, BobRun, BobRunReport } from "../api/bobReports";
import { severityRank } from "./ledgerFormat";

export const SEVERITIES = ["BLOCK", "FLAG", "SUGGEST"] as const;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Bob writes SQLite's UTC "YYYY-MM-DD HH:MM:SS"; an ISO string reads too. Null for anything else. */
export function parseBobTime(raw: string | null | undefined): Date | null {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00Z`
    : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(raw) ? `${raw.replace(" ", "T")}Z` : raw;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "4 Oct", in UTC like every time Bob stores. */
export function fmtDay(raw: string | null | undefined): string {
  const d = parseBobTime(raw);
  return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : "";
}

/** "2 Oct 22:11" */
export function fmtWhen(raw: string | null | undefined): string {
  const d = parseBobTime(raw);
  return d ? `${fmtDay(raw)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` : "";
}

/** "24 Aug 2026, 15:19 UTC" */
export function fmtDateTime(raw: string | null | undefined): string {
  const d = parseBobTime(raw);
  return d ? `${fmtDay(raw)} ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC` : "";
}

/** Whole days between two Bob times, or null when either is unreadable. */
export function daysBetween(from: string | null | undefined, to: string | null | undefined): number | null {
  const a = parseBobTime(from);
  const b = parseBobTime(to);
  if (!a || !b) return null;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000));
}

export function usd(n: number): string {
  return n >= 1000
    ? `$${Math.round(n).toLocaleString("en-US")}`
    : `$${n.toFixed(2)}`;
}

/**
 * The run that reviewed each head commit: a finding first seen or fixed at a
 * commit is new or fixed in that run's report. Of several runs at one head the
 * earliest that finished wins, else the earliest at all.
 */
export function runByHead(runs: ReadonlyArray<Pick<BobRun, "id" | "head_sha" | "error">>): Map<string, number> {
  const byHead = new Map<string, { id: number; failed: boolean }>();
  for (const r of [...runs].sort((a, b) => a.id - b.id)) {
    if (typeof r.head_sha !== "string" || r.head_sha === "" || !Number.isSafeInteger(r.id)) continue;
    const failed = r.error != null;
    const had = byHead.get(r.head_sha);
    if (!had || (had.failed && !failed)) byHead.set(r.head_sha, { id: r.id, failed });
  }
  return new Map([...byHead].map(([sha, r]) => [sha, r.id]));
}

/** "43 reviews, $343.91 spent"; the spend only when it is there and may be shown. */
export function reviewTally(stats: Pick<BobPosture, "runs_by_kind" | "spend_total_usd">, showSpend: boolean): string | null {
  const byKind = stats.runs_by_kind;
  if (!byKind || typeof byKind !== "object") return null;
  const n = Object.values(byKind).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
  const reviews = `${n} ${n === 1 ? "review" : "reviews"}`;
  return showSpend && typeof stats.spend_total_usd === "number" ? `${reviews}, ${usd(stats.spend_total_usd)} spent` : reviews;
}

/** Whole dollars from $10 up, for chart labels where cents are noise. */
export function usdShort(n: number): string {
  return n >= 10 ? `$${Math.round(n).toLocaleString("en-US")}` : `$${n.toFixed(2)}`;
}

/** "18.5%", or "none yet" when there is nothing to divide. */
export function pct(r: number | null): string {
  return r === null ? "none yet" : `${(r * 100).toFixed(1)}%`;
}

export function shortSha(sha: string | null | undefined): string {
  return typeof sha === "string" ? sha.slice(0, 7) : "";
}

function repoUrl(repo: string): string {
  return encodeURI(`https://github.com/${repo}`);
}

/** The range a push covered: a compare link when Bob kept the base, else the head commit. */
export function commitRangeHref(repo: string, base: string | null, head: string): string {
  return base
    ? `${repoUrl(repo)}/compare/${encodeURIComponent(base)}...${encodeURIComponent(head)}`
    : `${repoUrl(repo)}/commit/${encodeURIComponent(head)}`;
}

export function repoName(repo: string): string {
  return repo.split("/")[1] ?? repo;
}

export function sevCounts(findings: readonly LedgerFinding[]): Record<string, number> {
  const out: Record<string, number> = { BLOCK: 0, FLAG: 0, SUGGEST: 0 };
  for (const f of findings) out[f.severity] = (out[f.severity] ?? 0) + 1;
  return out;
}

/** "1 BLOCK, 1 FLAG" over the severities present, "all FLAG" when there is one, "" when none. */
export function sevMixText(counts: Record<string, number>): string {
  const present = SEVERITIES.filter((s) => (counts[s] ?? 0) > 0);
  if (present.length === 1) return `all ${present[0]}`;
  return present.map((s) => `${counts[s]} ${s}`).join(", ");
}

/** Worst for players first: severity, then a finding that names what a player sees, then age. */
export function worstFirst(findings: readonly LedgerFinding[]): LedgerFinding[] {
  const hasImpact = (f: LedgerFinding) => (typeof f.player_impact === "string" && f.player_impact.trim() !== "" ? 0 : 1);
  return [...findings].sort((a, b) =>
    severityRank(a.severity) - severityRank(b.severity) || hasImpact(a) - hasImpact(b) || a.id - b.id);
}

export interface FolderRow {
  folder: string;
  BLOCK: number;
  FLAG: number;
  SUGGEST: number;
  files: number;
  total: number;
}

/** A file's area: its first two directories, or "repo root" for a top-level file. */
export function folderOf(file: string): string {
  const dirs = file.split("/").filter(Boolean).slice(0, -1);
  return dirs.length === 0 ? "repo root" : dirs.slice(0, 2).join("/");
}

/** Findings grouped by area: most BLOCKs first, then most findings, then most FLAGs. */
export function byFolder(findings: readonly LedgerFinding[]): FolderRow[] {
  const rows = new Map<string, FolderRow & { fileSet: Set<string> }>();
  for (const f of findings) {
    const key = folderOf(f.file);
    const row = rows.get(key) ?? { folder: key, BLOCK: 0, FLAG: 0, SUGGEST: 0, files: 0, total: 0, fileSet: new Set<string>() };
    if (f.severity === "BLOCK" || f.severity === "FLAG" || f.severity === "SUGGEST") row[f.severity] += 1;
    row.total += 1;
    row.fileSet.add(f.file);
    rows.set(key, row);
  }
  return [...rows.values()]
    .map(({ fileSet, ...r }) => ({ ...r, files: fileSet.size }))
    .sort((a, b) => b.BLOCK - a.BLOCK || b.total - a.total || b.FLAG - a.FLAG || (a.folder < b.folder ? -1 : 1));
}

export interface WalkRow {
  file: string;
  added: number;
  fixed: number;
}

/** Files with a new or fixed finding in this run, the most touched first. */
export function walkthrough(report: BobRunReport): WalkRow[] {
  const rows = new Map<string, WalkRow>();
  const touch = (file: string, key: "added" | "fixed") => {
    const row = rows.get(file) ?? { file, added: 0, fixed: 0 };
    row[key] += 1;
    rows.set(file, row);
  };
  for (const f of report.findings.new) touch(f.file, "added");
  for (const f of report.findings.fixed) touch(f.file, "fixed");
  return [...rows.values()].sort((a, b) => b.added + b.fixed - (a.added + a.fixed) || (a.file < b.file ? -1 : 1));
}

export function fileLine(f: LedgerFinding): string {
  return f.line === null || f.line === undefined ? f.file : `${f.file}:${f.line}`;
}

export const PARTIAL_SEED = "partial seed:";

/** The reasons a whole-pack review stopped early, or null when it did not. */
export function partialSeedReason(run: Pick<BobRun, "error">): string | null {
  return typeof run.error === "string" && run.error.startsWith(PARTIAL_SEED)
    ? run.error.slice(PARTIAL_SEED.length).trim()
    : null;
}

export type RunState = "completed" | "partial" | "failed" | "displaced";

export function runState(run: Pick<BobRun, "error" | "displaced_by_seed">): RunState {
  if (partialSeedReason(run) !== null) return "partial";
  if (run.error) return "failed";
  if (run.displaced_by_seed) return "displaced";
  return "completed";
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One line per run for the activity feed and the runs table. */
export function runActivityText(run: BobRun): string {
  const at = shortSha(run.head_sha);
  if (run.kind === "seed") {
    const lead = `Whole-pack review at ${at}`;
    if (runState(run) === "failed") return `${lead} did not finish.`;
    return `${lead}: ${run.new_count} new, ${run.fixed_count} fixed${partialSeedReason(run) !== null ? ", stopped early" : ""}.`;
  }
  const lead = `${run.kind === "manual" ? "Manual review of" : "Push"} ${at}`;
  if (runState(run) === "failed") return `${lead} did not finish.`;
  const deferred = run.deferred > 0 ? `, ${plural(run.deferred, "re-check")} deferred` : "";
  return `${lead} reviewed: ${run.new_count} new, ${run.fixed_count} fixed${deferred}.`;
}

/** "Bob raised 3 new findings, confirmed 2 older findings are fixed, and left 79 open on dev."
 *  The run's own counters: the report's lists are credited to every run at the same commit. */
export function runSummary(report: BobRunReport): string {
  const { run } = report;
  const raised = run.new_count === 0 ? "Bob raised no new findings" : `Bob raised ${plural(run.new_count, "new finding")}`;
  const reopened = run.reopened_count > 0 ? `reopened ${run.reopened_count} that came back after a fix` : null;
  const fixed = run.fixed_count === 0 ? "confirmed none of the older ones fixed"
    : `confirmed ${plural(run.fixed_count, "older finding")} ${run.fixed_count === 1 ? "is" : "are"} fixed`;
  const after = openAfter(run);
  const parts = [raised, ...(reopened ? [reopened] : []), fixed];
  if (after !== null) return `${parts.join(", ")}, and left ${after} open on ${run.branch}.`;
  return parts.length === 2 ? `${parts[0]} and ${parts[1]}.` : `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}.`;
}

/** Open findings on the branch once the run finished, when Bob recorded the count it started from. */
export function openAfter(run: Pick<BobRun, "open_at_start" | "new_count" | "fixed_count" | "reopened_count">): number | null {
  return run.open_at_start === null || run.open_at_start === undefined
    ? null
    : Math.max(0, run.open_at_start + run.new_count + (run.reopened_count ?? 0) - run.fixed_count);
}

/** Polyline points for a sparkline in a w x h box with 2px of headroom. */
export function sparkPoints(values: readonly number[], w = 120, h = 40): string {
  if (values.length === 0) return "";
  const max = Math.max(...values, 1);
  const step = values.length === 1 ? 0 : w / (values.length - 1);
  return values.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - (v / max) * (h - 6)).toFixed(1)}`).join(" ");
}

/** A round axis top at or above `v`, with four evenly spaced ticks from it down to 0. */
export function axisTicks(v: number): number[] {
  const raw = Math.max(v, 3) / 3;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 1.5, 2, 2.5, 3, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  return [3, 2, 1, 0].map((i) => Math.round(i * step * 100) / 100);
}

/** Line and filled-area points for a series drawn into a 1000 x 240 box. */
export function trendGeometry(values: readonly number[], top: number): { line: string; area: string } {
  if (values.length === 0) return { line: "", area: "" };
  const xs = values.length === 1 ? [500] : values.map((_, i) => (i / (values.length - 1)) * 1000);
  const pts = values.map((v, i) => `${xs[i]!.toFixed(1)},${(240 - (v / (top || 1)) * 240).toFixed(1)}`);
  const line = pts.join(" ");
  return { line, area: `${xs[0]!.toFixed(1)},240 ${line} ${xs[xs.length - 1]!.toFixed(1)},240` };
}

/** Each segment's share of `total` as a CSS width, for a stacked severity bar. */
export function stackWidths(counts: Record<string, number>, total: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of SEVERITIES) out[s] = total > 0 ? `${((100 * (counts[s] ?? 0)) / total).toFixed(1)}%` : "0%";
  return out;
}

/** The run a `?run=<id>` link names, or null. */
export function runFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get("run")?.trim();
  if (!raw || !/^[1-9]\d*$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
}
